import { Check, Link2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wordmark } from "@/components/Sidebar";
import type { ModelNode } from "@/model/types";

/** What the share card shows: the hero number, its caption, and a simple curve. */
export interface ShareInfo {
	value: string;
	caption: string;
	/** Values along the curve, e.g. chance by month or the likely value by month. */
	curve: ArrayLike<number> | null;
	/** Fixed y-range (events use 0–1); otherwise the curve's own min and max. */
	yRange?: [number, number];
}

function Sparkline({ curve, yRange }: { curve: ArrayLike<number>; yRange?: [number, number] }) {
	const pts = Array.from(curve);
	if (pts.length < 2) return null;
	const lo = yRange ? yRange[0] : Math.min(...pts);
	const hi = yRange ? yRange[1] : Math.max(...pts);
	const span = hi - lo || 1;
	const W = 600;
	const H = 160;
	const d = pts.map((v, i) => `${i ? "L" : "M"}${((i / (pts.length - 1)) * W).toFixed(1)},${(H - ((v - lo) / span) * H).toFixed(1)}`).join("");
	return (
		<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full" aria-hidden>
			<path d={`M0,${H} H${W}`} stroke="currentColor" strokeOpacity="0.15" />
			<path d={`${d} L${W},${H} L0,${H} Z`} fill="var(--model)" fillOpacity="0.08" />
			<path d={d} fill="none" stroke="var(--model)" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
		</svg>
	);
}

/** Share: a 1200×630 card preview (PRD §17) and a copy-link button. */
export function ShareDialog({ open, onOpenChange, node, info }: { open: boolean; onOpenChange: (v: boolean) => void; node: ModelNode; info: ShareInfo }) {
	const [copied, setCopied] = useState(false);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Share {node.title}</DialogTitle>
					<DialogDescription>Anyone with the link sees the current forecast. This is the card that shows when the link is posted.</DialogDescription>
				</DialogHeader>
				<div className="flex aspect-[1200/630] w-full flex-col overflow-hidden rounded-xl border bg-card p-[5%] shadow-sm">
					<p className="line-clamp-2 text-xl leading-tight font-semibold tracking-tight sm:text-2xl">{node.title}</p>
					<div className="mt-2 flex items-baseline gap-3">
						<span className="text-4xl font-semibold tracking-tighter text-[var(--model)] tabular-nums sm:text-5xl">{info.value}</span>
						<span className="text-sm text-foreground/80 sm:text-base">{info.caption}</span>
					</div>
					<div className="mt-3 min-h-0 flex-1">{info.curve && <Sparkline curve={info.curve} yRange={info.yRange} />}</div>
					<div className="mt-3 flex items-center justify-between text-muted-foreground">
						<div className="origin-left scale-90">
							<Wordmark />
						</div>
						<span className="text-xs">Forecasts are opinions, not advice</span>
					</div>
				</div>
				<DialogFooter>
					<Button
						onClick={() => {
							void navigator.clipboard?.writeText(window.location.href);
							setCopied(true);
							toast("Link copied");
							setTimeout(() => setCopied(false), 1500);
						}}
					>
						{copied ? <Check className="size-4" /> : <Link2 className="size-4" />} Copy link
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
